// CHANGE: Removed all debug console statements for production
// Incident detail modal for admins
// Displays full incident information and allows status management
// Removed: Admin notes field (no longer needed)
// CHANGE: Added read-only mode for director and operator roles - can view details but cannot modify status
// UPDATED: Added file attachments display with preview grid and lightbox for images
// FIX: Pre-generate signed URLs for attachment thumbnails (bucket is private)
// ADDED: Collapsible history section showing status change logs (closed by default)
// UPDATED: Removed UserX icon (vestige hr category)
//
// UX/UI REFINEMENTS (Professional UX Optimization):
// 1. Fixed double category icon bug - removed duplicate icon in category metadata row
// 2. Status section extracted from metadata grid - separated actionable content from informational content for clear visual hierarchy
// 3. Attachment thumbnails optimized - 4-column grid with fixed h-24 height instead of 3-column aspect-square (reduces visual dominance)
// 4. Attachment hover effects - added scale transform and zoom icon overlay for better interactivity cues (Jakob's Law compliance)
// 5. Description label simplified - removed self-evident "Description" label (parcimonie informationnelle)
// 6. Improved metadata label legibility - increased from text-[11px] to text-xs and text-gray-400 to text-gray-500 (WCAG AA contrast)
// 7. Tightened vertical spacing - reduced space-y-4 to space-y-3 and removed pt-2 from section dividers for consistency
// 8. Status updates happen inline without closing the modal - toast notification for status changes
// 9. File attachments displayed with preview grid (images and PDFs) - lightbox for full-screen image viewing, PDFs open in new tab
//
// STRUCTURE: Metadata grid (Date, User, Site, Category) → Status Section (separated) → Description → Attachments → History Accordion

import { useState, useEffect } from 'react';
import { Users, PackageX, Wrench, HelpCircle, AlertTriangle, User, MapPin, Calendar, Paperclip, FileText, X as XIcon, ChevronDown, Clock, Maximize2 } from 'lucide-react';
import { Modal } from '../ui/Modal';
import {
  updateIncidentStatus,
  getStatusColorClass,
  getCategoryInfo,
  getStatusInfo,
  getIncidentHistory,
  INCIDENT_STATUSES
} from '../../lib/incidents';
import type { IncidentWithDetails, Incident, IncidentHistoryEntry } from '../../lib/incidents';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../contexts/AuthContext';
import { getFullName } from '../../lib/supabase';
import { CustomSelect, type SelectOption } from '../ui/CustomSelect';
import {
  getIncidentAttachments,
  getSignedUrl,
  isImageFile,
  isPdfFile,
  type IncidentAttachment
} from '../../lib/attachments';

interface IncidentDetailModalProps {
  incident: IncidentWithDetails;
  onClose: () => void;
}

const ICON_MAP = {
  Users,
  PackageX,
  Wrench,
  HelpCircle
} as const;

export function IncidentDetailModal({ incident, onClose }: IncidentDetailModalProps) {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [isUpdating, setIsUpdating] = useState(false);
  const [currentIncident, setCurrentIncident] = useState(incident);
  const [attachments, setAttachments] = useState<IncidentAttachment[]>([]);
  const [isLoadingAttachments, setIsLoadingAttachments] = useState(false);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [history, setHistory] = useState<IncidentHistoryEntry[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  // Check if current user is director or operator (read-only mode)
  const isReadOnly = user?.role === 'director' || user?.role === 'operator';

  // Load attachments when modal opens
  useEffect(() => {
    const loadAttachments = async () => {
      setIsLoadingAttachments(true);
      try {
        const data = await getIncidentAttachments(incident.id);
        setAttachments(data);

        // Pre-generate signed URLs for all attachments in parallel
        const urlPromises = data.map(async (attachment) => {
          try {
            const signedUrl = await getSignedUrl(attachment.file_url);
            return { id: attachment.id, url: signedUrl };
          } catch (error) {
            console.error(`Error generating signed URL for ${attachment.file_name}:`, error);
            return { id: attachment.id, url: null };
          }
        });

        const urls = await Promise.all(urlPromises);
        const urlMap = urls.reduce((acc, { id, url }) => {
          if (url) acc[id] = url;
          return acc;
        }, {} as Record<string, string>);

        setSignedUrls(urlMap);
      } catch (error) {
        console.error('Error loading attachments:', error);
      } finally {
        setIsLoadingAttachments(false);
      }
    };

    loadAttachments();
  }, [incident.id]);

  // Load incident history when modal opens
  useEffect(() => {
    const loadHistory = async () => {
      setIsLoadingHistory(true);
      try {
        const result = await getIncidentHistory(incident.id);
        if (result.success && result.data) {
          setHistory(result.data);
        }
      } catch (error) {
        console.error('Error loading incident history:', error);
      } finally {
        setIsLoadingHistory(false);
      }
    };

    loadHistory();
  }, [incident.id]);

  const categoryInfo = getCategoryInfo(currentIncident.category);
  const statusInfo = getStatusInfo(currentIncident.status);
  const CategoryIcon = categoryInfo ? ICON_MAP[categoryInfo.icon as keyof typeof ICON_MAP] : AlertTriangle;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const formatted = new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
    // Replace space with " à " to get "JJ/MM/AAAA à HH:MM" format
    return formatted.replace(/(\d{2}\/\d{2}\/\d{4})\s+(\d{2}:\d{2})/, '$1 à $2');
  };

  const handleStatusChange = async (newStatus: Incident['status']) => {
    setIsUpdating(true);
    try {
      const result = await updateIncidentStatus(currentIncident.id, newStatus);

      if (result.success && result.data) {
        setCurrentIncident(prev => ({
          ...prev,
          status: result.data!.status,
          resolved_at: result.data!.resolved_at,
          resolved_by: result.data!.resolved_by,
        }));
        showToast('Statut mis à jour avec succès', 'success');

        // Reload history to show the new status change
        const historyResult = await getIncidentHistory(currentIncident.id);
        if (historyResult.success && historyResult.data) {
          setHistory(historyResult.data);
        }
      } else {
        showToast(result.error || 'Erreur lors de la mise à jour', 'error');
      }
    } catch (error) {
      showToast('Erreur lors de la mise à jour', 'error');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleAttachmentClick = (attachment: IncidentAttachment) => {
    const signedUrl = signedUrls[attachment.id];

    if (!signedUrl) {
      showToast('URL du fichier non disponible', 'error');
      return;
    }

    if (isImageFile(attachment.file_type)) {
      setLightboxImage(signedUrl);
    } else if (isPdfFile(attachment.file_type)) {
      window.open(signedUrl, '_blank');
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Détail de l'incident"
      size="lg"
    >
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
        {/* Metadata Block - 2-column grid for compactness */}
        <div className="grid grid-cols-2 gap-x-6 gap-y-2">
          {/* Created at */}
          <div className="flex items-start gap-2">
            <Calendar size={16} className="text-gray-400 mt-0.5" />
            <div>
              <div className="text-xs text-gray-500">Date de création</div>
              <div className="text-xs font-medium text-gray-900">{formatDate(currentIncident.created_at)}</div>
            </div>
          </div>

          {/* User */}
          <div className="flex items-start gap-2">
            <User size={16} className="text-gray-400 mt-0.5" />
            <div>
              <div className="text-xs text-gray-500">Signalé par</div>
              <div className="text-xs font-medium text-gray-900">{getFullName(currentIncident.user)}</div>
            </div>
          </div>

          {/* Site */}
          <div className="flex items-start gap-2">
            <MapPin size={16} className="text-gray-400 mt-0.5" />
            <div>
              <div className="text-xs text-gray-500">Site</div>
              <div className="text-xs font-medium text-gray-900">{currentIncident.site.name}</div>
            </div>
          </div>

          {/* Category - Icon + text only (no duplicate icon) */}
          <div className="flex items-start gap-2">
            <CategoryIcon size={16} className="text-gray-400 mt-0.5" />
            <div>
              <div className="text-xs text-gray-500">Thématique</div>
              <div className="text-xs font-medium text-gray-900">{categoryInfo?.label}</div>
            </div>
          </div>
        </div>

        {/* Status Section - Separated from metadata for visual hierarchy */}
        {!isReadOnly && (
          <div className="border-t border-gray-100 pt-3">
            <div className="text-xs text-gray-500 mb-2">Statut de l'incident</div>
            <CustomSelect
              value={currentIncident.status}
              onChange={(value) => handleStatusChange(value as Incident['status'])}
              options={INCIDENT_STATUSES.map(status => ({
                value: status.value,
                label: status.label
              }))}
              disabled={isUpdating}
              renderOption={(option) => {
                const statusColor = getStatusColorClass(option.value);
                return (
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${statusColor.includes('green') ? 'bg-green-500' : statusColor.includes('yellow') ? 'bg-yellow-500' : statusColor.includes('blue') ? 'bg-blue-500' : 'bg-gray-500'}`} />
                    <span>{option.label}</span>
                  </div>
                );
              }}
              renderValue={(option) => {
                const statusColor = getStatusColorClass(option.value);
                return (
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${statusColor.includes('green') ? 'bg-green-500' : statusColor.includes('yellow') ? 'bg-yellow-500' : statusColor.includes('blue') ? 'bg-blue-500' : 'bg-gray-500'}`} />
                    <span>{option.label}</span>
                  </div>
                );
              }}
            />
            {isUpdating && (
              <div className="flex items-center gap-2 mt-2 text-xs text-gray-600">
                <div className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                <span>Mise à jour...</span>
              </div>
            )}
          </div>
        )}

        {isReadOnly && (
          <div className="border-t border-gray-100 pt-3">
            <div className="text-xs text-gray-500 mb-2">Statut de l'incident</div>
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColorClass(currentIncident.status)}`}>
              {statusInfo?.label}
            </span>
          </div>
        )}

        {/* Description - Simplified without label */}
        <div className="border-t border-gray-100 pt-3">
          <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-900 leading-relaxed">
            {currentIncident.description}
          </div>
        </div>

        {/* Attachments - Compact 4-column grid with hover effects */}
        {isLoadingAttachments ? (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
            <span>Chargement des pièces jointes...</span>
          </div>
        ) : attachments.length > 0 ? (
          <div className="border-t border-gray-100 pt-3">
            <div className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
              <Paperclip size={14} />
              <span>Pièces jointes ({attachments.length})</span>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {attachments.map((attachment) => {
                const isImage = isImageFile(attachment.file_type);
                const isPdf = isPdfFile(attachment.file_type);
                const signedUrl = signedUrls[attachment.id];

                return (
                  <button
                    key={attachment.id}
                    type="button"
                    onClick={() => handleAttachmentClick(attachment)}
                    className="relative group h-24 rounded-lg overflow-hidden border-2 border-gray-200 bg-gray-50 hover:border-primary-400 transition-all duration-200"
                  >
                    {isImage ? (
                      signedUrl ? (
                        <>
                          <img
                            src={signedUrl}
                            alt={attachment.file_name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                            loading="lazy"
                          />
                          {/* Hover overlay with zoom icon */}
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                            <Maximize2 size={20} className="text-white" />
                          </div>
                        </>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <div className="w-6 h-6 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                        </div>
                      )
                    ) : isPdf ? (
                      <div className="w-full h-full flex flex-col items-center justify-center p-2 text-center">
                        <FileText size={24} className="text-red-500 mb-1" />
                        <span className="text-[10px] text-gray-600 line-clamp-2 break-all">
                          {attachment.file_name}
                        </span>
                      </div>
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center p-2 text-center">
                        <FileText size={24} className="text-gray-400 mb-1" />
                        <span className="text-[10px] text-gray-600 line-clamp-2 break-all">
                          {attachment.file_name}
                        </span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}

        {/* History Accordion - Consistent spacing */}
        <div className="border-t border-gray-100 pt-3">
          <button
            onClick={() => setIsHistoryOpen(!isHistoryOpen)}
            className="w-full flex items-center text-sm font-medium text-gray-900 hover:text-gray-700 transition-colors"
          >
            <span>Historique</span>
            <ChevronDown
              size={18}
              className={`ml-2 text-gray-500 transition-transform duration-200 ${
                isHistoryOpen ? 'rotate-180' : ''
              }`}
            />
          </button>

          {/* Accordion Content */}
          <div
            className={`overflow-hidden transition-all duration-300 ease-in-out ${
              isHistoryOpen ? 'max-h-[500px] opacity-100 mt-3' : 'max-h-0 opacity-0'
            }`}
          >
            {isLoadingHistory ? (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                <span>Chargement de l'historique...</span>
              </div>
            ) : history.length > 0 ? (
              <div className="space-y-3">
                {history.map((entry) => (
                  <div key={entry.id} className="flex items-start gap-2">
                    <Clock size={16} className="text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900">{entry.action_description}</p>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                        <span>{formatDate(entry.changed_at)}</span>
                        <span>•</span>
                        <span>{entry.changed_by_name}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">Aucun historique disponible</p>
            )}
          </div>
        </div>

      </div>

      {/* Lightbox for image preview */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-all duration-200 text-white"
            aria-label="Fermer"
          >
            <XIcon size={24} />
          </button>
          <img
            src={lightboxImage}
            alt="Aperçu"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </Modal>
  );
}
